/**
 * Unified time-period mechanism shared by the MIS / report surfaces (Reports & MIS,
 * Portfolio Data > Returns, and the coming client-document build). One preset set,
 * one control, one window resolver — replacing the three near-copies that had
 * drifted (Reports/Returns anchored windows on the calendar; FeeHistory on the
 * last-complete calendar month).
 *
 * Windows anchor on the DATA SPAN (first/last month WITH data), never the calendar,
 * so: "Last 3m" is 3 months that actually have data; the range ends at the real
 * horizon (no false zero at month-start); and "Max" spans inception -> latest.
 * Each consumer supplies its OWN span — the firm AuM span (GET /reports/data-span)
 * for MIS/Returns, a per-agreement span for a client report. Months are 'YYYY-MM'
 * (lexicographically ordered, so string compare == chronological compare).
 */
import { Group, SegmentedControl } from '@mantine/core';
import type { MantineSize } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';

export type PeriodPreset = '3m' | '6m' | '12m' | 'ytd' | 'since' | 'max';

/** First / last month WITH data, as 'YYYY-MM' (null when the scope has no data). */
export interface DataSpan {
  first: string | null;
  last: string | null;
}

/** Shift a 'YYYY-MM' month by `delta` months (UTC-safe, normalises overflow). */
function addMonths(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, (m - 1) + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** A JS Date -> 'YYYY-MM' (local fields, matching the MonthPickerInput value). */
export function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * The inclusive ['YYYY-MM' from, to] window for a preset, anchored on the data span.
 * Trailing "Last Nm" and YTD END at `span.last` (the data horizon) and are floored
 * at `span.first`; "Max" is the whole span; "Since" runs from the picked month
 * (clamped into the span) to the horizon. Returns null when there's no data, or when
 * 'Since' has no month picked yet.
 */
export function resolveWindow(
  preset: PeriodPreset, since: Date | null, span: DataSpan,
): { from: string; to: string } | null {
  const { first, last } = span;
  if (!first || !last) return null;                       // no data in scope
  const floor = (m: string): string => (m < first ? first : m);
  switch (preset) {
    case 'max':  return { from: first, to: last };
    case '3m':   return { from: floor(addMonths(last, -2)), to: last };
    case '6m':   return { from: floor(addMonths(last, -5)), to: last };
    case '12m':  return { from: floor(addMonths(last, -11)), to: last };
    case 'ytd':  return { from: floor(`${last.slice(0, 4)}-01`), to: last };
    case 'since': {
      if (!since) return null;
      const s = monthKey(since);
      const from = s < first ? first : (s > last ? last : s);
      return { from, to: last };
    }
  }
}

/** The shared period control: presets (incl. Max) + the "Since" month picker. */
export function PeriodRangeSelect({
  preset, onPreset, since, onSince, size = 'xs',
}: {
  preset: PeriodPreset;
  onPreset: (p: PeriodPreset) => void;
  since: Date | null;
  onSince: (d: Date | null) => void;
  size?: MantineSize;
}) {
  return (
    <Group align="flex-end" wrap="wrap" gap="md">
      <SegmentedControl
        size={size}
        value={preset}
        onChange={(v) => onPreset(v as PeriodPreset)}
        data={[
          { label: 'Last 3m', value: '3m' },
          { label: 'Last 6m', value: '6m' },
          { label: 'Last 12m', value: '12m' },
          { label: 'YTD', value: 'ytd' },
          { label: 'Max', value: 'max' },
          { label: 'Since…', value: 'since' },
        ]}
      />
      {preset === 'since' && (
        <MonthPickerInput
          size={size} placeholder="Start month" value={since} onChange={onSince} maw={170}
        />
      )}
    </Group>
  );
}
